import { createColumnHelper } from "@tanstack/react-table";
import {
	formatLeverage,
	formatPrice,
	formatSignedUsd,
	formatSize,
	toNumber,
} from "#/lib/formatters";

export type PortfolioPositionRow = {
	coin: string;
	szi: string | number | null | undefined;
	leverage: { type: string; value: number };
	entryPx: string | number | null | undefined;
	unrealizedPnl: string | number | null | undefined;
	liquidationPx: string | number | null | undefined;
};

const col = createColumnHelper<PortfolioPositionRow>();

export function createPortfolioPositionColumns(t: (key: string) => string) {
	return [
		col.accessor("coin", {
			header: () => t("portfolio.market"),
			cell: (info) => (
				<span className="text-label2-medium">{info.getValue()}-PERP</span>
			),
		}),
		col.display({
			id: "side",
			header: () => t("portfolio.side"),
			cell: ({ row }) => {
				const size = toNumber(row.original.szi);
				const isLong = size > 0;
				return (
					<span
						className={`text-label2-medium ${isLong ? "text-trade-green" : "text-trade-red"}`}
					>
						{isLong ? t("general.long") : t("general.short")}
					</span>
				);
			},
		}),
		col.display({
			id: "size",
			header: () => t("portfolio.size"),
			cell: ({ row }) => (
				<span className="text-right text-label2-tabular-medium">
					{formatSize(Math.abs(toNumber(row.original.szi)))}
				</span>
			),
		}),
		col.display({
			id: "leverage",
			header: () => t("portfolio.leverage"),
			cell: ({ row }) => (
				<span className="text-right text-label2-tabular-medium">
					{formatLeverage(row.original.leverage.value)}
				</span>
			),
		}),
		col.accessor("entryPx", {
			header: () => t("portfolio.entry"),
			cell: (info) => (
				<span className="text-right text-label2-tabular-medium">
					{formatPrice(info.getValue())}
				</span>
			),
		}),
		col.display({
			id: "pnl",
			header: () => t("portfolio.pnl"),
			cell: ({ row }) => {
				const pnl = toNumber(row.original.unrealizedPnl);
				return (
					<span
						className={`text-right text-label2-tabular-medium ${pnl >= 0 ? "text-trade-green" : "text-trade-red"}`}
					>
						{formatSignedUsd(pnl)}
					</span>
				);
			},
		}),
		col.accessor("liquidationPx", {
			header: () => t("portfolio.liq-price"),
			cell: (info) => (
				<span className="text-right text-label2-tabular-medium text-muted-foreground">
					{formatPrice(info.getValue())}
				</span>
			),
		}),
	];
}
